import React, { useMemo, useRef, useState } from "react";
import { MdChevronRight, MdChevronLeft } from "react-icons/md";
import { getFlightLogo } from '../../utils/commonFunctions';
import Tooltip from '@mui/material/Tooltip';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTranslation } from 'react-i18next';


function buildCarrierInfo(searchResultData, carrierheading) {
  const info = {};
  searchResultData.forEach((result) => {
    const carrier = result.flightSummary[0]?.carrier;
    const carrierName = result.flightSummary[0]?.carrierName;
    if (carrier && carrierheading.includes(carrier) && !info[carrier]) {
      info[carrier] = {
        name: carrierName || carrier,
        logo: getFlightLogo(carrier),
      };
    }
  });
  carrierheading.forEach((carrier) => {
    if (!info[carrier]) {
      info[carrier] = {
        name: carrier,
        logo: getFlightLogo(carrier),
      };
    }
  });
  return info;
}

function buildStopLabels(noOfStopsMatrix = {}, { NonStop, OneStop, TwoPlusStop }) {
  const labels = {};
  Object.keys(noOfStopsMatrix).forEach((stop) => {
    if (stop === "0") labels[stop] = NonStop;
    else if (stop === "1") labels[stop] = OneStop;
    else labels[stop] = TwoPlusStop;
  });
  return labels;
}

function useCarriersPerPage() {
  const matches = useMediaQuery('(max-width:767px)');
  return matches ? 2 : 5;
}

const FlightMatrixTable = ({ matrixData, searchResultData, perPax, onFilterChange, onClearAll }) => {
  const { noOfStopsMatrix, carrierheading } = matrixData;
  const [tooltip, setTooltip] = useState("");

  // Slider logic
  const [scrollIndex, setScrollIndex] = useState(0);
  const carriersPerPage = useCarriersPerPage();
  const maxIndex = Math.max(0, carrierheading.length - carriersPerPage);
  const sliderRef = useRef(null);
  const { t } = useTranslation();

  const paginatedCarriers = carrierheading.slice(scrollIndex, scrollIndex + carriersPerPage);

  const carrierInfo = useMemo(
    () => buildCarrierInfo(searchResultData, carrierheading),
    [searchResultData, carrierheading]
  );
  const NonStop = t("NonStop");
  const OneStop = t("1 Stop");
  const TwoPlusStop = t("2+ Stop");

  const stopLabels = useMemo(
    () => buildStopLabels(noOfStopsMatrix, { NonStop, OneStop, TwoPlusStop }),
    [noOfStopsMatrix]
  );
  const stops = Object.keys(noOfStopsMatrix).sort((a, b) => Number(a) - Number(b));

  // Tooltip handlers
  const showTooltip = (e, text) => {
    setTooltip(text);
  };
  const hideTooltip = () => setTooltip("");


  // Slider navigation
  const scrollLeft = () => setScrollIndex((i) => Math.max(i - 1, 0));
  const scrollRight = () => setScrollIndex((i) => Math.min(i + 1, maxIndex));

  // Add new useMemo for global min/max fare calculation
  const { minFare, maxFare } = useMemo(() => {
    const allFares = [];
    stops.forEach(stop => {
      carrierheading.forEach(carrier => {
        const val = noOfStopsMatrix[stop][carrier];
        if (typeof val === 'number') {
          allFares.push(perPax(val));
        }
      });
    });
    let minFare = null, maxFare = null;
    if (allFares.length > 0) {
      minFare = Math.min(...allFares);
      maxFare = Math.max(...allFares);
    }
    return { minFare, maxFare };
  }, [stops, carrierheading, noOfStopsMatrix, perPax]);

  const [filterApplied, setFilterApplied] = useState(false);

  const handleFilterChange = (filter: { type: string; value: any }) => {
    setFilterApplied(true);
    onFilterChange(filter);
  };

  const handleClearAll = () => {
    setFilterApplied(false);
    onClearAll();
  };

  return (
    <div className="flight-matrix-table">
      
      <div className="specific_airline">
        {t("Searching for a specific airline")}
      </div>
      <div className="main-slider">
        {carrierheading.length > carriersPerPage && (
          <button
            onClick={scrollLeft}
            disabled={scrollIndex === 0}
            className="left-btn"
          >
            <MdChevronLeft />
          </button>
        )}
        <div className="slider-ref" ref={sliderRef}>
          <table>
            <thead>
              <tr>
                <th className="clear_all">
                  {filterApplied && (
                    <a href="#" 
                      onClick={(e) => {
                        e.preventDefault();
                        handleClearAll();
                      }}
                    >
                      {t("Clear All")}
                    </a>
                  )}
                </th>
                {paginatedCarriers.map((carrier, idx) => (
                  <th
                    key={carrier}
                    className={`carrier_filter ${idx === 0 ? "first-column" : ""}`}
                    onClick={() => handleFilterChange({ type: "carrier", value: carrier })}
                    onMouseEnter={e => showTooltip(e, `Show flights for ${carrierInfo[carrier]?.name || carrier}`)}
                    onMouseLeave={hideTooltip}
                  >
                    <div className="carrier_filter-content">
                    
                      {carrierInfo[carrier]?.logo && (
                        <img
                          src={carrierInfo[carrier].logo}
                          alt={carrierInfo[carrier].name}
                        />
                      )}
                      <div className="carrier_name">
                        <Tooltip title={tooltip} placement="bottom"  arrow>
                              {(carrierInfo[carrier]?.name || carrier).length > 18
                          ? (carrierInfo[carrier]?.name || carrier).slice(0, 15) + '...'
                          : (carrierInfo[carrier]?.name || carrier)}
                        </Tooltip>
                        
                      </div>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {stops.map((stop) => (
                <tr key={stop}>
                  <td
                    onClick={() => handleFilterChange({ type: "stop", value: stopLabels[stop] })}
                    onMouseEnter={e => showTooltip(e, `Filter by ${stopLabels[stop]}`)}
                    onMouseLeave={hideTooltip}
                    className="stop_labels"
                  >

                    <Tooltip title={tooltip} placement="bottom" arrow ><span>{stopLabels[stop]}</span></Tooltip>

                  </td>
                  {paginatedCarriers.map((carrier, idx) => {
                    const val = noOfStopsMatrix[stop][carrier];
                    let fareClass = '';
                    if (typeof val === 'number') {
                      const fare = perPax(val);
                      if (minFare !== null && fare === minFare) {
                        fareClass = 'fare-min';
                      } else if (maxFare !== null && fare === maxFare) {
                        fareClass = 'fare-max';
                      }
                    }
                    return (
                      <td
                        className={`pricing ${fareClass} ${idx === 0 ? "first-column" : ""} 
                        ${noOfStopsMatrix[stop][carrier] !== "-" ? "clickable" : "default-cursor"}`}
                        key={carrier}
                        onClick={() =>
                          noOfStopsMatrix[stop][carrier] !== "-" &&
                          handleFilterChange({ type: "price", value: { carrier, stop: stopLabels[stop] } })
                        }

                        onMouseEnter={e =>
                          noOfStopsMatrix[stop][carrier] !== "-" &&
                          showTooltip(e, `Show ${stopLabels[stop]} flights for ${carrierInfo[carrier]?.name || carrier}`)
                        }
                        onMouseLeave={hideTooltip}
                      >
                        <Tooltip title={tooltip} placement="bottom" arrow >
                              <span>
                                {typeof val === 'number' ? `$${perPax(val).toFixed(2)}` : '-'}
                              </span>
                        </Tooltip>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {carrierheading.length > carriersPerPage && (
          <button
            onClick={scrollRight}
            disabled={scrollIndex === maxIndex}
            className="right-btn"
          >
            <MdChevronRight />
          </button>
        )}
      </div>
    </div>
  );
};

export default FlightMatrixTable;